/**
 * Anticipos (adelantos) de una venta.
 *
 * El anticipo es UNA línea con el ítem de anticipo del catálogo de facturación.
 * Ese ítem NO se escribe en el código: cada empresa (tenant) tiene su propio
 * registro, con su id y su unidad de medida (NIU para bienes, ZZ para servicios
 * del catálogo 03 de SUNAT). Acá solo vive la convención de `internal_id`, y el
 * registro real se resuelve en runtime contra la búsqueda de productos de la
 * sede (GET /ms/migraciones/v1.0/productos/search).
 *
 * La vendedora elige producto o servicio y edita el detalle y el monto. La venta
 * recuerda sus anticipos para deducirlos en la factura final; un anticipo ya
 * deducido no se vuelve a ofrecer.
 *
 * Contrato verificado contra SUNAT (ver odd/tasks/anticipos.md): el anticipo se
 * emite con `pago_anticipado` y la factura final lo deduce con su número, la base
 * sin IGV y el total.
 */
import { parseMonto } from '@/utils/montos'

/**
 * Convención de códigos internos: igual en todos los tenants, para poder
 * resolver el registro de cada empresa sin configurar nada a mano.
 */
export const CODIGOS_ANTICIPO = {
    producto: 'ANTICIPO-PRODUCTO',
    servicio: 'ANTICIPO-SERVICIO'
}

/** Detalle con el que abre el formulario. */
export const DETALLE_ANTICIPO = 'ANTICIPO'

/** Texto mínimo que acepta la búsqueda de productos del BFF (4 caracteres). */
export const TEXTO_BUSQUEDA_ANTICIPO = 'ANTICIPO'

const codigoNormalizado = (valor) => String(valor === undefined || valor === null ? '' : valor).trim().toUpperCase()

const unidadNormalizada = (valor, porDefecto) => {
    const unidad = codigoNormalizado(valor)
    return unidad === '' ? porDefecto : unidad
}

/**
 * De la búsqueda de productos del tenant, devuelve el ítem de anticipo de bienes
 * y el de servicios tal como están cargados en esa empresa (id y unidad reales).
 *
 * @param {Array} items resultado de `productos/searchProductos`
 * @returns {{ producto: object|null, servicio: object|null }}
 */
export const resolverItemsAnticipo = (items) => {
    const lista = Array.isArray(items) ? items : []

    const buscar = (codigo, unidadPorDefecto) => {
        const encontrado = lista.find((x) => x && codigoNormalizado(x.internal_id) === codigo)
        if (!encontrado) return null

        const descripcion = String(encontrado.description || encontrado.name || codigo).trim()
        return {
            internal_id: encontrado.internal_id,
            unit_type_id: unidadNormalizada(encontrado.unit_type_id, unidadPorDefecto),
            description: descripcion === '' ? codigo : descripcion
        }
    }

    return {
        producto: buscar(CODIGOS_ANTICIPO.producto, 'NIU'),
        servicio: buscar(CODIGOS_ANTICIPO.servicio, 'ZZ')
    }
}

/**
 * Ítem con el que abre el formulario: producto (NIU) si la empresa lo tiene, si
 * no el de servicio. `null` cuando la empresa no tiene ninguno cargado.
 */
export const itemAnticipoPorDefecto = (resueltos) =>
    (resueltos && (resueltos.producto || resueltos.servicio)) || null

/** Texto para el selector: distingue bien de servicio por la unidad del catálogo 03. */
export const etiquetaItemAnticipo = (item) => {
    if (!item) return ''
    const unidad = unidadNormalizada(item.unit_type_id, '')
    if (unidad === 'ZZ') return 'Servicio (ZZ)'
    if (unidad === 'NIU') return 'Producto (NIU)'
    return unidad === '' ? 'Sin unidad' : `Unidad ${unidad}`
}

const dosDecimales = (valor) => Number(Number(valor).toFixed(2))

/** Monto del anticipo, en número y con 2 decimales; 0 cuando no es un monto real. */
export const montoAnticipoNumero = (valor) => {
    const monto = parseMonto(valor)
    return monto > 0 ? dosDecimales(monto) : 0
}

/** El detalle es obligatorio: sin texto no hay línea que emitir. */
export const detalleAnticipoValido = (valor) => typeof valor === 'string' && valor.trim() !== ''

/** Base sin IGV de un total (el IGV peruano es 18%). */
export const baseSinIgv = (total) => {
    const monto = parseMonto(total)
    return monto > 0 ? dosDecimales(monto / 1.18) : 0
}

/**
 * Línea de productos que viaja en el payload del anticipo, o null si falta el
 * detalle, el monto o el ítem del tenant. Misma forma que las líneas de la venta.
 *
 * `item` es el registro resuelto por `resolverItemsAnticipo`: la línea lleva su
 * `internal_id` real y `idTipoServicio` se deriva de su unidad (ZZ = servicio),
 * que es lo que el BFF traduce a la unidad del comprobante.
 */
export const buildItemAnticipo = (payload) => {
    const { detalle, monto, idTipoMoneda, item } = payload || {}
    const descripcion = typeof detalle === 'string' ? detalle.trim() : ''
    const montoLinea = montoAnticipoNumero(monto)
    const codigoItem = item ? String(item.internal_id || '').trim() : ''
    if (!descripcion || montoLinea <= 0 || codigoItem === '') return null

    return {
        internal_id: codigoItem,
        description: descripcion,
        price: montoLinea,
        comision: 0,
        descuentos: 0,
        unidades: 1,
        subTotal: montoLinea,
        idTipoMoneda: idTipoMoneda || 1,
        idTipoServicio: unidadNormalizada(item.unit_type_id, 'NIU') === 'ZZ' ? 1 : 0
    }
}

const numeroAnticipo = (valor) => (typeof valor === 'string' ? valor.trim().toUpperCase() : '')

/**
 * Agrega el anticipo recién emitido a la lista guardada en la venta.
 * No duplica por número y no guarda registros sin número o sin monto.
 */
export const registrarAnticipo = (lista, dato) => {
    const actuales = Array.isArray(lista) ? lista : []
    const { numero, total, totalBase, fecha } = dato || {}
    const numeroLimpio = numeroAnticipo(numero)
    const montoTotal = montoAnticipoNumero(total)
    if (!numeroLimpio || montoTotal <= 0) return actuales
    if (actuales.some((x) => x.numero === numeroLimpio)) return actuales

    const montoBase = montoAnticipoNumero(totalBase) || baseSinIgv(montoTotal)
    return [...actuales, { numero: numeroLimpio, total: montoTotal, monto: montoBase, fecha: fecha || null, deducido: false }]
}

/** Anticipos que todavía se pueden deducir en una factura final. */
export const anticiposDeducibles = (lista) => (Array.isArray(lista) ? lista.filter((x) => !x.deducido) : [])

/**
 * Payload `anticipos` de la factura final: solo los seleccionados que siguen
 * pendientes, con la base sin IGV y el total (lo que espera ms-migraciones).
 */
export const construirAnticiposParaFactura = (lista, seleccionados) => {
    const pendientes = anticiposDeducibles(lista)
    const elegidos = Array.isArray(seleccionados) ? seleccionados : []
    const elegidosPendientes = pendientes.filter((x) => elegidos.includes(x.numero))
    return elegidosPendientes.map((x) => ({ numero: x.numero, monto: x.monto, total: x.total }))
}

/** Marca los anticipos deducidos para que no se vuelvan a ofrecer. */
export const marcarDeducidos = (lista, seleccionados) => {
    const elegidos = Array.isArray(seleccionados) ? seleccionados : []
    return (Array.isArray(lista) ? lista : []).map((x) => (elegidos.includes(x.numero) ? { ...x, deducido: true } : x))
}
